import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiClock, FiAlertCircle, FiDollarSign, FiX } = FiIcons;

const ProblemSection = () => {
  const problems = [
    {
      icon: FiCalendar,
      title: 'Calendar Full of Duds',
      description: 'Your schedule is packed with calls that go nowhere. Discovery calls with prospects who "need to think about it" or "can\'t afford it right now."'
    },
    {
      icon: FiClock,
      title: 'No Time for Real Work',
      description: 'You\'re too busy with sales calls to focus on client delivery and strategic growth. The very success you seek is being blocked by unqualified prospects.'
    },
    {
      icon: FiX,
      title: 'Leaky Funnel',
      description: 'Potential high-ticket clients leave your site because they can\'t get their specific questions answered immediately. They move on to your competitors.'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 py-2 mb-6">
            <SafeIcon icon={FiAlertCircle} className="text-red-600" />
            <span className="text-sm font-semibold text-red-700">The Hidden Cost of Wasted Time</span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            Your Most Valuable Asset Is <span className="text-blue-600 relative">
              Time
              <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 200 8">
                <path d="M0 4 Q50 0, 100 4 T200 4" stroke="currentColor" strokeWidth="3" fill="none" className="text-blue-600"/>
              </svg>
            </span>
          </h2>
          <p className="text-2xl text-gray-700 mb-8 font-semibold">
            Are You Spending It on the <span className="text-red-600">Wrong People?</span>
          </p>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            You became a coach to make an impact, not to spend your days on endless "discovery calls" with prospects who aren't a good fit. 
            Every hour you spend with an unqualified lead is an hour you can't spend serving your best clients or growing your business.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="bg-white p-8 rounded-2xl border-2 border-red-100 hover:border-red-300 hover:shadow-2xl transition-all duration-300 relative group"
            >
              <div className="bg-gradient-to-br from-red-50 to-orange-50 w-20 h-20 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                <SafeIcon icon={problem.icon} className="text-3xl text-red-600" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                {problem.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed text-center">
                {problem.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Cost Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto bg-gradient-to-r from-red-600 to-orange-600 rounded-3xl p-10 text-white text-center shadow-2xl"
        >
          <SafeIcon icon={FiDollarSign} className="text-6xl mx-auto mb-6" />
          <h3 className="text-3xl lg:text-4xl font-bold mb-6">The Real Cost of Doing Nothing</h3>
          <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
            If your billable rate is $200/hour, <span className="font-bold text-white">10 hours of wasted calls per week</span> costs you 
            <span className="font-bold text-yellow-300"> $8,000 a month</span> in lost revenue and opportunity.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className="text-5xl font-bold mb-2">10 hrs</p>
              <p className="text-red-100">Wasted weekly on unqualified leads</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className="text-5xl font-bold mb-2">$8,000</p>
              <p className="text-red-100">Lost revenue monthly (at $200/hr)</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <p className="text-5xl font-bold mb-2">$96K</p>
              <p className="text-red-100">Lost annually</p>
            </div>
          </div>
          <p className="text-2xl font-bold">
            That's a <span className="text-yellow-300">$96,000/year bottleneck</span> keeping your business from scaling.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;